import { useFormContext } from "react-hook-form";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";
import type { BidPostData } from "validations";

import { ControlledIntegerInput } from "@krea/shared/design-system/ControlledInputs/IntegerInput";
import { MoneyInput } from "@krea/shared/design-system/ControlledInputs/MoneyInput";
import type { Currency } from "@krea/shared/types/common";

import type { IRevolvingCreditBidInput } from "@/features/lender-responses/types/lender-response";

import DetailsItem from "../../../../details/item";

interface IFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const RevolvingCreditForm = ({
  bidFormIndex,
  currency,
  isEditing,
  isInterestRatePerYearActivated,
}: IFormProps) => {
  const [, calculatedCurrency] = useFormatCurrency({
    currency,
  });

  const { control } = useFormContext<BidPostData<IRevolvingCreditBidInput>>();

  return (
    <div className="flex w-full flex-col">
      <div className="w-full">
        <DetailsItem
          title={
            isEditing
              ? "Update revolving credit terms"
              : "Revolving credit terms"
          }
          className="mb-0"
        >
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-4">
              {/* amount */}

              <MoneyInput
                name={`bids.${bidFormIndex}.amount`}
                label="Amount"
                control={control}
                required
                currency={calculatedCurrency!}
              />

              {/* monthly_amortisation */}
              <ControlledIntegerInput
                name={`bids.${bidFormIndex}.monthlyAmortisation`}
                label="Amortisation (per month)"
                control={control}
                required
                rightText="%"
                placeholder='Value in "%"'
              />

              {/* lender_monthly_interest */}
              <ControlledIntegerInput
                name={`bids.${bidFormIndex}.lenderMonthlyInterest`}
                label={
                  isInterestRatePerYearActivated
                    ? "Interest (per year)"
                    : "Interest (per month)"
                }
                control={control}
                required
                rightText="%"
                placeholder='Value in "%"'
              />
            </div>
          </div>
        </DetailsItem>

        <DetailsItem title="">
          <div className="flex w-full flex-col gap-4">
            <div className="mb-4 mt-2">
              <span
                className="italic text-gray-600"
                style={{ fontSize: "14px" }}
              >
                Here you can explain other costs & qualitative information about
                your offer
              </span>
            </div>

            <div className="flex w-full flex-col gap-4">
              {/* withdrawal_fee */}
              <ControlledIntegerInput
                name={`bids.${bidFormIndex}.withdrawalFee`}
                label="Withdrawal fee"
                control={control}
                required
                rightText="%"
                placeholder='Value in "%"'
              />

              {/* setup_fee */}
              <MoneyInput
                name={`bids.${bidFormIndex}.setupFee`}
                label="Setup fee"
                control={control}
                currency={calculatedCurrency!}
              />

              {/* limit_fee_per_year */}
              <ControlledIntegerInput
                name={`bids.${bidFormIndex}.limitFeePerYear`}
                label="Limit fee (per year)"
                control={control}
                rightText="%"
                placeholder='Value in "%"'
              />

              {/* postal_fee */}
              <MoneyInput
                name={`bids.${bidFormIndex}.postalFee`}
                label="Postal fee (per month)"
                control={control}
                currency={calculatedCurrency!}
              />
            </div>
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
